/* MBN-276 — PDP media gallery: two-up landscape frame.
 *
 * <duo-gallery> drives the carousel half of the frame.
 *
 * Desktop (>= 990px) the lead image is static and the carousel runs over every
 * *other* image, shown one at a time in the right pane. Under 990px the list is
 * a native scroll-snap row over *all* the images, so swipe is the browser's and
 * this element only moves the scroll position and reads it back.
 *
 * The <ul> is the same one `product-info.js` rewrites on a variant change, so a
 * MutationObserver re-reads it rather than caching the slide list.
 */

if (!customElements.get('duo-gallery')) {
  customElements.define(
    'duo-gallery',
    class DuoGallery extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;

        // An element whose definition is already registered is upgraded at its
        // start tag, before its children are parsed — which is what happens
        // whenever this section is re-rendered into an existing document (the
        // theme editor, a quick-add fetch). Wait for the children rather than
        // silently giving up on them.
        this.list = this.querySelector('[data-duo-list]');
        if (!this.list) {
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true });
          } else {
            requestAnimationFrame(() => this.connectedCallback());
          }
          return;
        }
        this.initialised = true;

        this.frame = this.querySelector('[data-duo-frame]');
        this.nav = this.querySelector('[data-duo-nav]');
        this.counter = this.querySelector('[data-duo-counter]');
        this.counterCurrent = this.querySelector('[data-duo-current]');
        this.counterTotal = this.querySelector('[data-duo-total]');
        this.desktop = window.matchMedia('(min-width: 990px)');
        this.index = 0;
        this.dragged = false;

        this.onPrev = () => this.step(-1);
        this.onNext = () => this.step(1);
        this.onScroll = () => this.readScroll();
        this.onKeydown = (event) => this.handleKeydown(event);
        this.onBreakpoint = () => this.reset();

        this.querySelector('[data-duo-prev]')?.addEventListener('click', this.onPrev);
        this.querySelector('[data-duo-next]')?.addEventListener('click', this.onNext);
        this.list.addEventListener('scroll', this.onScroll, { passive: true });
        this.addEventListener('keydown', this.onKeydown);
        this.desktop.addEventListener('change', this.onBreakpoint);

        this.bindDrag();

        // product-info.js adds, removes and reorders the <li>s on a variant
        // change, and media-gallery.js prepends the variant's featured image so
        // it becomes the lead. Re-read rather than trusting a cached list.
        this.observer = new MutationObserver(() => this.reset());
        this.observer.observe(this.list, { childList: true });

        this.refresh();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.list?.removeEventListener('scroll', this.onScroll);
        this.removeEventListener('keydown', this.onKeydown);
        this.desktop?.removeEventListener('change', this.onBreakpoint);
      }

      /** Every item; on desktop the lead is static, so it is not a slide. */
      get slides() {
        const items = Array.from(this.list.querySelectorAll('.pdp-duo__item'));
        return this.desktop.matches ? items.slice(1) : items;
      }

      reset() {
        this.index = 0;
        this.list.scrollLeft = 0;
        this.refresh();
      }

      refresh() {
        const total = this.slides.length;

        // One slide needs no controls — a two-image product on desktop, or a
        // single-image product at any width.
        if (this.nav) this.nav.hidden = total <= 1;
        if (this.counter) this.counter.hidden = total <= 1;
        if (this.counterTotal) this.counterTotal.textContent = String(total);

        if (this.index > total - 1) this.index = Math.max(total - 1, 0);
        this.apply(true);
      }

      apply(scroll) {
        const slides = this.slides;
        if (!slides.length) return;

        if (this.desktop.matches) {
          slides.forEach((slide, i) => {
            slide.classList.toggle('pdp-duo__item--current', i === this.index);
          });
        } else if (scroll) {
          // Every slide is exactly one viewport wide, so the offset is exact
          // and needs no offsetLeft read against a positioned ancestor.
          this.list.scrollTo({
            left: this.index * this.list.clientWidth,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
        }

        if (this.counterCurrent) this.counterCurrent.textContent = String(this.index + 1);
      }

      step(delta) {
        const total = this.slides.length;
        if (total <= 1) return;
        this.index = (this.index + delta + total) % total;
        this.apply(true);
      }

      /** Keeps the counter honest when the customer swipes instead of tapping. */
      readScroll() {
        if (this.desktop.matches || !this.list.clientWidth) return;
        const index = Math.round(this.list.scrollLeft / this.list.clientWidth);
        if (index === this.index) return;
        this.index = Math.min(Math.max(index, 0), Math.max(this.slides.length - 1, 0));
        this.apply(false);
      }

      handleKeydown(event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (this.slides.length <= 1) return;
        event.preventDefault();
        this.step(event.key === 'ArrowRight' ? 1 : -1);
      }

      /* Desktop has no scroll-snap track to swipe, so a horizontal pointer drag
       * across the frame steps the carousel. A drag still ends in a click on the
       * image's modal toggle, which would open the lightbox on top of the
       * navigation — so the click straight after a drag is swallowed.
       */
      bindDrag() {
        if (!this.frame) return;
        let startX = 0;
        let startY = 0;
        let tracking = false;

        this.frame.addEventListener('pointerdown', (event) => {
          this.dragged = false;
          if (!this.desktop.matches || event.pointerType === 'mouse' && event.button !== 0) return;
          tracking = true;
          startX = event.clientX;
          startY = event.clientY;
        });

        this.frame.addEventListener('pointerup', (event) => {
          if (!tracking) return;
          tracking = false;
          const dx = event.clientX - startX;
          const dy = event.clientY - startY;
          if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
          this.dragged = true;
          this.step(dx < 0 ? 1 : -1);
        });

        this.frame.addEventListener('pointercancel', () => {
          tracking = false;
        });

        this.frame.addEventListener(
          'click',
          (event) => {
            if (!this.dragged) return;
            this.dragged = false;
            event.preventDefault();
            event.stopPropagation();
          },
          true
        );
      }
    }
  );
}
